import { Box3, Vector3 } from 'three';

import { isValidNumber } from '../../../utils/math/number';
import { CoordinateManager } from '../../coordinate-manager';
import { ProjectGeodataManager } from '../../project-geodata-manager';

/**
 * The max distance in meters an object
 * should be from origo
 */
const maxDistanceFromOrigo = 1000;

/**
 * Checks if the bounding box is further away than the max distance from origo.
 */
const isBoundingBoxFarFromOrigo = (bb: Box3) => {
  const length = bb.min.length();
  return isValidNumber(length) ? length > maxDistanceFromOrigo : false;
};

/**
 * Checks if the bounding box is further away than the max distance from origo.
 */
export const isBoundingBoxWithinValidRange = (bb: Box3) => {
  const coordinateManager = CoordinateManager.getInstance();

  // No geographic range to validate against when the project has no coordinate system.
  if (!coordinateManager.hasCoordinateSystem()) {
    return true;
  }

  try {
    const min = coordinateManager.localToProjectCoords(bb.min);
    const max = coordinateManager.localToProjectCoords(bb.max);
    return coordinateManager.isValidCoordinate(min) && coordinateManager.isValidCoordinate(max);
  } catch {
    return false;
  }
};

/**
 * Whether the scene origo should be updated before focusing on the given bounding box.
 * In the CAD viewer, or when the project has no coordinate system, assets can be placed
 * far from the scene origin and need an origo shift before the camera can focus on them.
 */
export const shouldUpdateSceneOrigo = (bb: Box3, isCadViewer: boolean): boolean => {
  return (
    (isCadViewer || !CoordinateManager.getInstance().hasCoordinateSystem()) &&
    isBoundingBoxFarFromOrigo(bb)
  );
};

/**
 * Updates the scene origo based on a local coordinate in the scene.
 *
 * This will trigger all assets to re-position,
 * and it also triggers the timeline data to re-position
 */
export const updateSceneOrigo = (localPosition: Vector3) => {
  const origo = CoordinateManager.getInstance().getProjectionOrigo();

  if (!origo) {
    return;
  }

  const newOrigo = localPosition.clone().add(origo);

  CoordinateManager.getInstance().setProjectProjectionData(
    ProjectGeodataManager.instance().getProjectProjection() ?? 'local',
    newOrigo,
  );
};
